import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Loader2, DollarSign, TrendingUp, 
  Wallet, Receipt, Ship, CreditCard,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import api from '../lib/api';

export default function ClientPaymentsPage() {
  const { user, hasField } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    loadBalance();
  }, []);

  const loadBalance = async () => {
    setLoading(true);
    try {
      const res = await api.get('/payments/my-balance');
      setBalance(res.data);
    } catch (err) {
      toast.error(t('common.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!hasField('view_amounts')) return '—';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-payments-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-heading tracking-tight">Mi Balance</h1>
          <p className="text-sm text-muted-foreground">Resumen de costos y pagos de tus embarques</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {balance?.initial_balance > 0 && (
            <div className="bg-card border border-border rounded-sm p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Receipt size={16} />
                <span className="text-xs uppercase tracking-wide">Saldo Inicial</span>
              </div>
              <p className="text-2xl font-heading text-amber-400">{formatCurrency(balance.initial_balance)}</p>
              {balance.initial_balance_date && (
                <p className="text-xs text-muted-foreground">al {formatDate(balance.initial_balance_date)}</p>
              )}
            </div>
          )}
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Ship size={16} />
              <span className="text-xs uppercase tracking-wide">Costo Embarques</span>
            </div>
            <p className="text-2xl font-heading text-amber-400">{formatCurrency(balance?.shipments_cost)}</p>
            <p className="text-xs text-muted-foreground">{balance?.shipment_count || 0} embarques</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Wallet size={16} />
              <span className="text-xs uppercase tracking-wide">Total Pagado</span>
            </div>
            <p className="text-2xl font-heading text-emerald-400">{formatCurrency(balance?.total_paid)}</p>
            <p className="text-xs text-muted-foreground">{balance?.payment_count || 0} pagos</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign size={16} />
              <span className="text-xs uppercase tracking-wide">Saldo Pendiente</span>
            </div>
            <p className={`text-2xl font-heading ${(balance?.balance || 0) > 0 ? 'text-red-400' : (balance?.balance || 0) < 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
              {formatCurrency(Math.abs(balance?.balance || 0))}
            </p>
            <p className="text-xs text-muted-foreground">
              {balance?.balance > 0 ? 'Por pagar' : balance?.balance < 0 ? 'A tu favor' : 'Sin saldo'}
            </p>
          </div>
        </div>

        {/* Two columns: Shipments and Payments */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Shipments */}
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <div className="bg-secondary/30 px-4 py-3 border-b border-border">
              <h2 className="font-medium flex items-center gap-2">
                <Ship size={16} className="text-blue-400" />
                Mis Embarques ({balance?.shipments?.length || 0})
              </h2>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-secondary/50">
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 px-4">MBL</th>
                    <th className="py-2 px-4">Estatus</th>
                    <th className="py-2 px-4 text-right">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {!balance?.shipments?.length ? (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-muted-foreground">
                        Sin embarques
                      </td>
                    </tr>
                  ) : (
                    balance.shipments.map((s) => (
                      <tr key={s.mbl} className="border-b border-border/30 hover:bg-secondary/30">
                        <td className="py-2 px-4">
                          <Link to={`/shipments/${s.mbl}`} className="text-primary hover:underline font-mono text-xs">
                            {s.mbl}
                          </Link>
                        </td>
                        <td className="py-2 px-4">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            s.status_general_code === 'ENTREGADO' ? 'bg-emerald-500/20 text-emerald-400' :
                            s.status_general_code === 'EN_PUERTO' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {s.status_general_code || 'PENDIENTE'}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-right text-amber-400">{formatCurrency(s.total_cost)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payments */}
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <div className="bg-secondary/30 px-4 py-3 border-b border-border">
              <h2 className="font-medium flex items-center gap-2">
                <CreditCard size={16} className="text-emerald-400" />
                Mis Pagos ({balance?.payments?.length || 0})
              </h2>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-secondary/50">
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 px-4">Fecha</th>
                    <th className="py-2 px-4">Método</th>
                    <th className="py-2 px-4 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {!balance?.payments?.length ? (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-muted-foreground">
                        Sin pagos registrados
                      </td>
                    </tr>
                  ) : (
                    balance.payments.map((p) => (
                      <tr key={p.id} className="border-b border-border/30 hover:bg-secondary/30">
                        <td className="py-2 px-4">{formatDate(p.payment_date)}</td>
                        <td className="py-2 px-4">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            p.payment_method === 'TRANSFERENCIA' ? 'bg-blue-500/20 text-blue-400' :
                            p.payment_method === 'EFECTIVO' ? 'bg-emerald-500/20 text-emerald-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {p.payment_method}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-right text-emerald-400">{formatCurrency(p.amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Balance Summary */}
        {balance?.balance !== 0 && (
          <div className={`rounded-sm p-4 ${balance?.balance > 0 ? 'bg-red-500/10 border border-red-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {balance?.balance > 0 ? (
                  <ArrowUpRight className="text-red-400" size={24} />
                ) : (
                  <ArrowDownRight className="text-emerald-400" size={24} />
                )}
                <div>
                  <p className={`text-lg font-heading ${balance?.balance > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {balance?.balance > 0 ? 'Saldo Pendiente' : 'Saldo a tu Favor'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {balance?.balance > 0 
                      ? 'Este es el monto que tienes pendiente por pagar' 
                      : 'Tienes un crédito a tu favor'}
                  </p>
                </div>
              </div>
              <p className={`text-3xl font-heading ${balance?.balance > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {formatCurrency(Math.abs(balance?.balance))}
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
